import { ImagesSubComponent } from '../images.sub.component';
import { getCurrentMajorBreakpoint, MajorBreakpoint } from '../../../_common/client/breakpoint';
import { ZoomImageElement } from '../../../_feature-components/zoom-image/zoom-image.element';
import { FALLBACK_IMAGE_ID, getImageUrl } from '../../../_common/client/images';
import { MainImageVideo } from './main-image-video';
import { OcCarouselV1Events } from '@otto-ec/otto-components/carousel';

export class MainImageSubComponent extends ImagesSubComponent {
    public rootSelector = '.js_pdp_main-image';

    private imageHasBeenChanged = false;
    private isMobile = false;

    private currentIndex = 0;

    private videoIndex = Number.MAX_SAFE_INTEGER;

    private video?: MainImageVideo;

    public async onHtmlLoaded(): Promise<void> {
        if (this.isPresent()) {
            this.currentIndex = 0;
            this.isMobile = getCurrentMajorBreakpoint() === MajorBreakpoint.Mobile;
            this.getCarouselElement().addEventListener('oc-item-visibility-changed', event => this.handleItemVisibilityChangedEvent(event));
            if (this.isMobile) {
                this.activatePageIndicator();
                this.initializeVideo();
            }
            this.handleSlideChange(0, false);
            this.imageHasBeenChanged = false;
        }
    }

    protected onImageChanged(index: number): void {
        const adjustedIndex = index >= this.videoIndex ? index + 1 : index;
        this.getCarouselElement().moveTo(adjustedIndex, { reducedMotion: true });

        this.trackStageChange(index, 'thumbnail');
    }

    private activatePageIndicator() {
        this.getCarouselElement().removeAttribute('hide-pagination-dots');
    }

    private initializeVideo() {
        const videoAssetKey = this.getRootElement().dataset.videoAssetKey;
        if (videoAssetKey) {
            const slideElements = this.getChildElements<HTMLElement>('.js_pdp_main-image__slide');
            this.videoIndex = slideElements.length < 5 ? slideElements.length : 4;

            const { slideElement, slideContentElement } = this.createSlide();
            slideElement.setAttribute('data-oc-carousel-v1.item-type', 'video');
            slideElements[this.videoIndex - 1].insertAdjacentElement('afterend', slideElement);

            this.video = new MainImageVideo(videoAssetKey, slideContentElement);
        } else {
            this.videoIndex = Number.MAX_SAFE_INTEGER;
        }
    }

    private createSlide() {
        const slideElement = document.createElement('div');
        slideElement.className = 'pdp_main-image__slide js_pdp_main-image__slide';

        const slideContentWrapperElement = document.createElement('div');
        slideContentWrapperElement.className = 'pdp_main-image__slide-content-wrapper';

        const slideContentElement = document.createElement('div');
        slideContentElement.className = 'pdp_main-image__slide-content';

        slideContentWrapperElement.appendChild(slideContentElement);
        slideElement.appendChild(slideContentWrapperElement);

        return {
            slideElement,
            slideContentElement,
        };
    }

    private handleSlideChange(currentIndex: number, emitToOtherSubcomponents: boolean) {
        const previousIndex = this.currentIndex;
        const nextIndex = currentIndex + 1;

        this.currentIndex = currentIndex;

        if (previousIndex === this.videoIndex) {
            this.video?.pause();
        }

        if (currentIndex === this.videoIndex) {
            this.video?.init();
        } else {
            this.loadImageForSlide(currentIndex);
        }

        if (nextIndex !== this.videoIndex) {
            this.loadImageForSlide(nextIndex);
        }

        if (emitToOtherSubcomponents && currentIndex !== this.videoIndex) {
            const adjustedIndex = currentIndex >= this.videoIndex ? currentIndex - 1 : currentIndex;
            this.changeImage(adjustedIndex);
        }
    }

    private handleItemVisibilityChangedEvent(event: OcCarouselV1Events['oc-item-visibility-changed']) {
        const index = event.detail.visibleItem.index;
        const scrollSource = event.detail.scrollSource;
        if (event.detail.stageIsScrolling) {
            this.handleSlideChange(index, scrollSource !== 'move-method');
        } else {
            if (scrollSource !== 'move-method') {
                this.trackStageChange(index, scrollSource === 'pagination-dots' ? 'bullet' : 'swipe');
            }
        }
    }

    private trackStageChange(index: number, method: 'bullet' | 'swipe' | 'thumbnail') {
        const imageActivity = this.imageHasBeenChanged ? 'open_following' : 'open_first';
        const imageCount = this.getChildElements('.js_pdp_main-image__slide').length;
        if (index === this.videoIndex) {
            this.trackEvent({
                product_Video: 'open',
                product_DifferentImageMethod: method,
            });
        } else {
            const adjustedIndex = index >= this.videoIndex ? index - 1 : index;
            const adjustedImageCount = imageCount > this.videoIndex ? imageCount - 1 : imageCount;
            this.trackEvent({
                product_DifferentImageActivity: imageActivity,
                product_DifferentImageChange: `${adjustedIndex + 1}|${adjustedImageCount}`,
                product_DifferentImageMethod: method,
            });
            this.imageHasBeenChanged = true;
        }
    }

    private getSlide(index: number) {
        const slideElementSelector = `${this.rootSelector} .js_pdp_main-image__slide:nth-child(${index + 1})[data-image-loaded="false"]`;
        const slideElement = document.querySelector<HTMLElement>(slideElementSelector);
        const slideContentElement = slideElement ? slideElement.querySelector('.js_pdp_main-image__slide-content') : null;
        return {
            slideElement,
            slideContentElement,
        };
    }

    private loadImageForSlide(index: number): void {
        const { slideElement, slideContentElement } = this.getSlide(index);
        if (slideElement && slideContentElement) {
            const imageElement = this.isMobile
                ? this.createZoomImageElementFromSlide(slideElement)
                : this.createImageElementFromSlide(slideElement);
            slideContentElement.appendChild(imageElement);
            slideElement.dataset.imageLoaded = 'true';
        }
    }

    private createImageElementFromSlide(slideElement: HTMLElement): HTMLImageElement {
        const alt = slideElement.dataset.imageAlt || '';
        const imageId = slideElement.dataset.imageId || FALLBACK_IMAGE_ID;
        const currentMajorBreakpoint = getCurrentMajorBreakpoint();
        const imageHeight = currentMajorBreakpoint === MajorBreakpoint.Mobile ? 600 : 520;
        const imageWidth = currentMajorBreakpoint === MajorBreakpoint.Mobile ? 900 : 551;

        const imageElement = document.createElement('img');
        imageElement.alt = alt;
        imageElement.className = 'pdp_main-image__image';
        imageElement.onerror = () => {
            imageElement.src = imageElement.src.replace(imageId, FALLBACK_IMAGE_ID);
            imageElement.srcset = imageElement.srcset.replace(imageId, FALLBACK_IMAGE_ID);
            imageElement.alt = 'ohne Abbildung';
            imageElement.onerror = null;
        };
        imageElement.src = getImageUrl(imageId, `h=${imageHeight}&w=${imageWidth}&sm=clamp&upscale=true&fmt=auto`);
        imageElement.srcset =
            getImageUrl(imageId, `h=${imageHeight * 2}&w=${imageWidth * 2}&qlt=40&unsharp=0,1,0.6,7&sm=clamp&upscale=true&fmt=auto`) +
            ' 2x';
        return imageElement;
    }

    private createZoomImageElementFromSlide(slideElement: HTMLElement): HTMLElement {
        const alt = slideElement.dataset.imageAlt || '';
        const imageId = slideElement.dataset.imageId || '';
        const currentMajorBreakpoint = getCurrentMajorBreakpoint();
        const imageHeight = currentMajorBreakpoint === MajorBreakpoint.Mobile ? '600' : '520';
        const imageWidth = currentMajorBreakpoint === MajorBreakpoint.Mobile ? '900' : '551';

        const imageElement = document.createElement('zoom-image');
        imageElement.setAttribute('data-image-alt', alt);
        imageElement.setAttribute('data-image-id', imageId);
        imageElement.setAttribute('data-image-height', imageHeight);
        imageElement.setAttribute('data-image-width', imageWidth);
        imageElement.classList.add('pdp_main-image__zoom-image');
        const dataImageClass = 'pdp_main-image__image';
        imageElement.setAttribute('data-image-class', dataImageClass);
        imageElement.addEventListener(ZoomImageElement.EVENT_OPENED, () => {
            this.trackEvent({
                product_DifferentImageActivity: 'pinchzoom',
            });
        });
        return imageElement;
    }

    private getCarouselElement() {
        return this.getChildElement<HTMLOcCarouselV1Element>('.js_pdp_main-image__carousel');
    }
}
